import { BaseButton } from '../base/button/button';
import { BaseInput } from '../base/input/input';
import React, { useState } from 'react';
import * as S from './roster-table.styles';

interface IAddTagFormProps {
    count: number;
    noun: string;
    onSubmit: (tagName: string) => Promise<void>;
    onCancel: () => void;
}

/** Name a tag and apply it to the selection. An existing tag of the same name is reused, not duplicated. */
export function AddTagForm({ count, noun, onSubmit, onCancel }: IAddTagFormProps): React.ReactElement {
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const valid = name.trim().length > 0;

    const submit = async (): Promise<void> => {
        if (!valid || saving) {
            return;
        }
        setSaving(true);
        try {
            await onSubmit(name.trim());
        } finally {
            setSaving(false);
        }
    };

    return (
        <S.DialogBody
            onSubmit={(e: React.FormEvent): void => {
                e.preventDefault();
                void submit();
            }}
        >
            <S.DialogText>
                Adds the tag to {count} {noun}. Records that already have it are left as they are.
            </S.DialogText>
            <BaseInput
                autoFocus={true}
                value={name}
                placeholder="Tag name, e.g. credentialed"
                aria-label="Tag name"
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setName(e.target.value)}
            />
            <S.DialogButtons>
                <BaseButton type="button" variant="outlined" onClick={onCancel}>
                    Cancel
                </BaseButton>
                <BaseButton type="submit" variant="primary" disabled={!valid || saving}>
                    {saving ? 'Adding…' : 'Add tag'}
                </BaseButton>
            </S.DialogButtons>
        </S.DialogBody>
    );
}

interface IDisableFormProps {
    count: number;
    noun: string;
    onSubmit: () => Promise<void>;
    onCancel: () => void;
}

/**
 * Confirm a Disable. The API has no delete — `disable*` is soft — but it is not harmless: the claim
 * editor refuses to submit a claim that references an inactive provider or facility
 * (`addInactiveEntityBlockers`). The operator has to know that before confirming.
 */
export function DisableForm({ count, noun, onSubmit, onCancel }: IDisableFormProps): React.ReactElement {
    const [saving, setSaving] = useState(false);

    const submit = async (): Promise<void> => {
        setSaving(true);
        try {
            await onSubmit();
        } finally {
            setSaving(false);
        }
    };

    return (
        <S.DialogBody
            onSubmit={(e: React.FormEvent): void => {
                e.preventDefault();
                void submit();
            }}
        >
            <S.DialogText>
                {count} {noun} will be removed from the roster and can no longer be chosen for new claims.
            </S.DialogText>
            <S.DialogWarning>
                Existing claims that reference them cannot be submitted from the claim editor until the {noun} are
                replaced on those claims.
            </S.DialogWarning>
            <S.DialogButtons>
                <BaseButton type="button" variant="outlined" onClick={onCancel}>
                    Cancel
                </BaseButton>
                <BaseButton type="submit" variant="warning" disabled={saving}>
                    {saving ? 'Disabling…' : `Disable ${count}`}
                </BaseButton>
            </S.DialogButtons>
        </S.DialogBody>
    );
}
